import React, { useState, useEffect, useMemo } from 'react';
import { History, TrendingUp, TrendingDown, Package, AlertCircle, ChevronLeft, ChevronRight, Calendar, RefreshCw, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { getStockHistory } from '../api/products';

const ITEMS_PER_PAGE = 10;

const StockHistoryView = ({ productId, branchId = null }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('all'); // 'today', 'week', 'month', 'year', 'custom', 'all'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    fetchHistory();
  }, [productId, branchId]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [dateFilter, customStartDate, customEndDate]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getStockHistory(productId);
      // Backend returns array directly
      let historyData = Array.isArray(response) ? response : [];
      
      // Filter by branch if branchId is provided (for branch admins)
      if (branchId) {
        historyData = historyData.filter(activity => activity.branch_id === branchId);
      }
      
      setHistory(historyData);
    } catch (err) {
      setError('Failed to load stock history');
      toast.error('Failed to load stock history');
    } finally {
      setLoading(false);
    }
  };

  // Date filter helper functions
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case 'today':
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1) };
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 7);
        return { start: weekStart, end: now };
      case 'month':
        const monthStart = new Date(today);
        monthStart.setMonth(today.getMonth() - 1);
        return { start: monthStart, end: now };
      case 'year':
        const yearStart = new Date(today);
        yearStart.setFullYear(today.getFullYear() - 1);
        return { start: yearStart, end: now };
      case 'custom':
        return {
          start: customStartDate ? new Date(customStartDate) : null,
          end: customEndDate ? new Date(customEndDate + 'T23:59:59') : null
        };
      default:
        return { start: null, end: null };
    }
  };

  // Filter history by date range
  const filteredHistory = useMemo(() => {
    if (dateFilter === 'all') return history;
    
    const { start, end } = getDateRange(dateFilter);
    if (!start && !end) return history;
    
    return history.filter(activity => {
      const activityDate = new Date(activity.created_at);
      if (start && activityDate < start) return false;
      if (end && activityDate > end) return false;
      return true;
    });
  }, [history, dateFilter, customStartDate, customEndDate]);

  // Pagination calculations (now using filteredHistory)
  const totalPages = Math.ceil(filteredHistory.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedHistory = filteredHistory.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const resetFilters = () => {
    setDateFilter('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setCurrentPage(1);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'initial_stock':
        return <Package className="w-4 h-4 text-blue-600" />;
      case 'stock_addition':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'sale':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      case 'adjustment':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'return':
        return <TrendingUp className="w-4 h-4 text-purple-600" />;
      default:
        return <History className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActivityLabel = (type) => {
    const labels = {
      initial_stock: 'Initial Stock',
      stock_addition: 'Stock Addition',
      sale: 'Sale',
      adjustment: 'Adjustment',
      return: 'Return'
    };
    return labels[type] || type;
  };

  const getActivityColor = (type) => {
    const colors = {
      initial_stock: 'bg-blue-50 text-blue-700 border-blue-200',
      stock_addition: 'bg-green-50 text-green-700 border-green-200',
      sale: 'bg-red-50 text-red-700 border-red-200',
      adjustment: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      return: 'bg-purple-50 text-purple-700 border-purple-200'
    };
    return colors[type] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
        <p className="text-gray-600">{error}</p>
        <button
          onClick={fetchHistory}
          className="mt-4 text-sm text-blue-600 hover:text-blue-800"
        >
          Try again
        </button>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-8">
        <History className="w-12 h-12 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-600">No stock activity history</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <History className="w-5 h-5" />
            Stock Activity History
          </h3>
          <span className="text-sm text-gray-500">
            {filteredHistory.length} of {history.length} activities
          </span>
        </div>

        {/* Date Filter Controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Preset Buttons */}
          <div className="flex flex-wrap gap-1">
            {[
              { key: 'today', label: 'Today' },
              { key: 'week', label: 'Week' },
              { key: 'month', label: 'Month' },
              { key: 'year', label: 'Year' },
              { key: 'all', label: 'All' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setDateFilter(key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                  dateFilter === key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
            <button
              onClick={() => setDateFilter('custom')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors flex items-center gap-1 ${
                dateFilter === 'custom'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Calendar className="w-3 h-3" />
              Custom
            </button>
          </div>

          {/* Reset Button */}
          {dateFilter !== 'all' && (
            <button
              onClick={resetFilters}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-300 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              Reset
            </button>
          )}
        </div>

        {/* Custom Date Range Inputs */}
        {dateFilter === 'custom' && (
          <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 font-medium">From:</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 font-medium">To:</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3">
        {paginatedHistory.map((activity) => (
          <div key={activity.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getActivityColor(
                  activity.activity_type
                )}`}
              >
                {getActivityIcon(activity.activity_type)}
                {getActivityLabel(activity.activity_type)}
              </span>
              <span className="text-xs text-gray-500">
                {formatDate(activity.created_at)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500 mb-1">Change</p>
                <p className={`font-semibold ${
                  activity.quantity_change > 0
                    ? 'text-green-600'
                    : activity.quantity_change < 0
                    ? 'text-red-600'
                    : 'text-gray-600'
                }`}>
                  {activity.quantity_change > 0 ? '+' : ''}
                  {activity.quantity_change}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Balance</p>
                <p className="font-medium text-gray-900">{activity.new_stock}</p>
              </div>
            </div>

            {activity.reason && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Reason</p>
                <p className="text-sm text-gray-600">{activity.reason}</p>
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500">Performed by: <span className="text-gray-700 font-medium">{activity.created_by_name || 'System'}</span></p>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date & Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Activity Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quantity Change
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Balance After
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reason/Notes
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Performed By
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedHistory.map((activity) => (
              <tr key={activity.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(activity.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getActivityColor(
                      activity.activity_type
                    )}`}
                  >
                    {getActivityIcon(activity.activity_type)}
                    {getActivityLabel(activity.activity_type)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`text-sm font-semibold ${
                      activity.quantity_change > 0
                        ? 'text-green-600'
                        : activity.quantity_change < 0
                        ? 'text-red-600'
                        : 'text-gray-600'
                    }`}
                  >
                    {activity.quantity_change > 0 ? '+' : ''}
                    {activity.quantity_change}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                  {activity.new_stock}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                  {activity.reason || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {activity.created_by_name || 'System'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Showing {startIndex + 1} - {Math.min(endIndex, history.length)} of {history.length}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockHistoryView;

